import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Text, Button, Space, Title, Modal } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import {
  DELETE_MANY_PRODUCTS,
  DELETE_MANY_ANATOMY_ITEMS,
  DELETE_MANY_REMOVABLE_ITEMS,
  DELETE_MANY_IMPLANT_ITEM,
  UPDATE_ANATOMY_ITEM,
  UPDATE_REMOVABLE_ITEM,
  UPDATE_IMPLANT_ITEM,
} from "../../../gql/";
import AnatomyItemComponent from "./AnatomyItemComponent";
import RemovableItemComponent from "./RemovableItemComponent";
import CreateProductComponent from "./CreateProductComponents";
import ImplantItemComponent from "./ImplantItemComponent";
import { UseAuth } from "/@/contexts";

import {
  Product,
  ItemType,
  STYLE,
  LabsBreadcrumbs,
  GET_ORDER_BY_UNIQUE_ATTRIBUTE,
  NEW_PAP_WORKFLOW_ACTIVATION_DATE,
  FULL_DENTURE_RDV2_CONTINUATION,
  PARTIAL_DENTURE_RDV2_CONTINUATION,
  getProductCreationLabel,
} from "@jasper/shared";
import { useV2ProductTypes } from "@jasper/product-workflows";
import { useOrderById } from "@jasper/use-cases";
import { ProductTypeEnum } from "@jasper/client-models";

const AnatomyItemView = ({
  product,
  onDelete,
  onUpdateTeeth,
}: {
  product: Product;
  onDelete: (itemId: string) => void;
  onUpdateTeeth: (itemId: string, teeth: number[]) => void;
}) => {
  return (
    <>
      <li>
        <span style={{ fontWeight: "bold" }}>Anatomy Items:&nbsp;</span>
      </li>
      {(product?.anatomyItem ?? []).map((item, index) => (
        <div style={{ marginLeft: "3rem" }} key={item.id}>
          <AnatomyItemComponent
            item={item}
            index={index}
            onDelete={() => onDelete(item.id)}
            onUpdateTeeth={(itemId, teeth) => onUpdateTeeth(itemId, teeth)}
          />
        </div>
      ))}
    </>
  );
};

const RemovableItemView = ({
  product,
  onDelete,
  onUpdateTeeth,
}: {
  product: Product;
  onDelete: (itemId: string) => void;
  onUpdateTeeth: (itemId: string, teeth: number[]) => void;
}) => {
  return (
    <>
      <li>
        <span style={{ fontWeight: "bold" }}>Removable Items:&nbsp;</span>
      </li>
      {(product?.removableItem ?? []).map((item, index) => (
        <div style={{ marginLeft: "3rem" }} key={item.id}>
          <RemovableItemComponent
            item={item}
            index={index}
            onDelete={() => onDelete(item.id)}
            onUpdateTeeth={(itemId, teeth) => onUpdateTeeth(itemId, teeth)}
          />
        </div>
      ))}
    </>
  );
};

const ImplantItemView = ({
  product,
  onDelete,
  onUpdateTeeth,
}: {
  product: Product;
  onDelete: (itemId: string) => void;
  onUpdateTeeth: (itemId: string, teeth: number[]) => void;
}) => {
  return (
    <>
      <li>
        <span style={{ fontWeight: "bold" }}>Implant Items:&nbsp;</span>
      </li>
      {(product?.implantItem ?? []).map((item, index) => (
        <div style={{ marginLeft: "3rem" }} key={item.id}>
          <ImplantItemComponent
            item={item}
            index={index}
            onDelete={() => onDelete(item.id)}
            onUpdateTeeth={(itemId, teeth) => onUpdateTeeth(itemId, teeth)}
          />
        </div>
      ))}
    </>
  );
};

const OrderEdit = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();

  const { isV2Product } = useV2ProductTypes();
  const { refetch: refetchOrder } = useOrderById({
    orderId: order_id ?? "",
    skip: true,
  });

  const { user } = UseAuth();

  const [isModalNewProductOpen, setIsModalNewProductOpen] =
    useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = useState<string>("");

  const [deleteManyProduct] = useMutation(DELETE_MANY_PRODUCTS);
  const [deleteManyAnatomyItems] = useMutation(DELETE_MANY_ANATOMY_ITEMS);
  const [deleteManyRemovableItems] = useMutation(DELETE_MANY_REMOVABLE_ITEMS);
  const [deleteManyImplantItems] = useMutation(DELETE_MANY_IMPLANT_ITEM);
  const [updateAnatomyItem] = useMutation(UPDATE_ANATOMY_ITEM);
  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM);
  const [updateImplantItem] = useMutation(UPDATE_IMPLANT_ITEM);

  const { data, refetch } = useQuery(GET_ORDER_BY_UNIQUE_ATTRIBUTE, {
    variables: {
      where: {
        id: order_id,
      },
    },
  });

  const deleteProduct = async (productId: string): Promise<void> => {
    const checkConfirm = confirm(
      "You are going to delete this product and all related items, this action is irreversible"
    );
    if (!checkConfirm) {
      notifications.show({
        title: "Deletion aborted",
        color: "red",
        message: "",
      });
      return;
    }
    try {
      await deleteManyProduct({
        variables: {
          where: {
            id: {
              equals: productId,
            },
          },
        },
      });
      notifications.show({
        title: "Product deleted",
        color: "green",
        message: "",
      });
      refetch();
    } catch (e) {
      notifications.show({
        title: "Error while deleting product",
        color: "red",
        message: `${e}`,
      });
    }
  };

  const deleteAnatomyItem = async (itemId: string): Promise<void> => {
    const checkConfirm = confirm(
      "You are going to delete this item, this action is irreversible"
    );
    if (!checkConfirm) {
      notifications.show({
        title: "Deletion aborted",
        color: "red",
        message: "",
      });
      return;
    }
    try {
      await deleteManyAnatomyItems({
        variables: {
          where: {
            id: {
              equals: itemId,
            },
          },
        },
      });
      notifications.show({
        title: "Item deleted",
        color: "green",
        message: "",
      });
      refetch();
    } catch (e) {
      notifications.show({
        title: "Error while deleting item",
        color: "red",
        message: `${e}`,
      });
    }
  };

  const deleteRemovableItem = async (itemId: string): Promise<void> => {
    const checkConfirm = confirm(
      "You are going to delete this item, this action is irreversible"
    );
    if (!checkConfirm) {
      notifications.show({
        title: "Deletion aborted",
        color: "red",
        message: "",
      });
      return;
    }
    try {
      await deleteManyRemovableItems({
        variables: {
          where: {
            id: {
              equals: itemId,
            },
          },
        },
      });
      notifications.show({
        title: "Item deleted",
        color: "green",
        message: "",
      });
      refetch();
    } catch (e) {
      notifications.show({
        title: "Error while deleting item",
        color: "red",
        message: `${e}`,
      });
    }
  };

  const deleteImplantItem = async (itemId: string): Promise<void> => {
    const checkConfirm = confirm(
      "You are going to delete this item, this action is irreversible"
    );
    if (!checkConfirm) {
      notifications.show({
        title: "Deletion aborted",
        color: "red",
        message: "",
      });
      return;
    }
    try {
      await deleteManyImplantItems({
        variables: {
          where: {
            id: {
              equals: itemId,
            },
          },
        },
      });
      notifications.show({
        title: "Item deleted",
        color: "green",
        message: "",
      });
      refetch();
    } catch (e) {
      notifications.show({
        title: "Error while deleting item",
        color: "red",
        message: `${e}`,
      });
    }
  };

  const product = (data?.getOrderByUniqueAttribute?.products ?? []).find(
    product => product.id === selectedProductId
  );

  const isRemovableItemType = (
    selectedProductId: string,
    watchProductType: ItemType
  ) => {
    const productType = watchProductType ?? product?.productType;
    return (
      productType === ItemType.FULL_DENTURE ||
      productType === ItemType.PARTIAL_DENTURE ||
      productType === ItemType.SPLINT ||
      (productType as string) === FULL_DENTURE_RDV2_CONTINUATION ||
      (productType as string) === PARTIAL_DENTURE_RDV2_CONTINUATION
    );
  };

  return (
    <>
      <Modal
        opened={isModalNewProductOpen}
        onClose={() => setIsModalNewProductOpen(false)}
        title="New product"
      >
        <CreateProductComponent
          closeModal={async ({
            redirectUserToEditProductPageEnabled,
            productType,
            productId,
            product,
            isRdv2Continuation,
          }) => {
            const isDenture =
              productType === ItemType.FULL_DENTURE ||
              productType === ItemType.PARTIAL_DENTURE;
            setIsModalNewProductOpen(false);

            if (
              isV2Product(productType) &&
              productId &&
              productType !== ProductTypeEnum.inlayCore
            ) {
              await refetchOrder();
              navigate(`/orders/${order_id}/configure/products/${productId}`);
              return;
            }

            if (isDenture && productId) {
              navigate(`/orders/${order_id}/stepped-edit/${productId}`, {
                state: { isRdv2Continuation },
              });
              return;
            }

            if (redirectUserToEditProductPageEnabled) {
              navigate(
                `/orders/${order_id}/edit/${productId}/edit?itemType=${productType}`,
                {
                  state: {
                    product,
                  },
                }
              );
            }
          }}
          orderId={order_id}
          product={product}
          refetch={refetch}
          isRemovableItem={watchProductType =>
            isRemovableItemType(selectedProductId, watchProductType)
          }
          order={data?.getOrderByUniqueAttribute}
        />
      </Modal>
      <LabsBreadcrumbs
        orderReference={data?.getOrderByUniqueAttribute?.orderReference}
        orderId={data?.getOrderByUniqueAttribute?.id}
        user={user}
      />
      {(data?.getOrderByUniqueAttribute.products ?? []).map(
        (product: Product, index: number) => {
          const creationLabel = getProductCreationLabel(product, {
            shouldTranslate: true,
            withCreator: true,
          });
          return (
            <div
              key={product.id}
              style={{
                border: "0.02rem solid lightgrey",
                padding: "1rem",
                margin: "1rem",
              }}
            >
              <Title order={2}>Product {index + 1}</Title>
              <Text>
                <span style={{ fontWeight: "bold" }}>Type:</span>{" "}
                {product.productType}
              </Text>
              <Text>
                <span style={{ fontWeight: "bold" }}>Teeth:</span>{" "}
                {product.teeth.join(", ")}
              </Text>
              {creationLabel && <Text>{creationLabel}</Text>}
              <Text
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => {
                  setSelectedProductId(product.id);
                  setIsModalNewProductOpen(true);
                }}
              >
                Update product teeth or type
              </Text>
              <Text
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => {
                  if (
                    isV2Product(product.productType) &&
                    product.productType !== ProductTypeEnum.inlayCore
                  ) {
                    navigate(
                      `/orders/${order_id}/configure/products/${product.id}`
                    );
                    return;
                  }
                  if (
                    new Date(
                      data?.getOrderByUniqueAttribute.createdAt
                    ).getTime() > NEW_PAP_WORKFLOW_ACTIVATION_DATE.getTime() &&
                    (product.productType === ItemType.PARTIAL_DENTURE ||
                      product.productType === ItemType.FULL_DENTURE)
                  ) {
                    navigate(`/orders/${order_id}/stepped-edit/${product.id}`);
                    return;
                  }
                  if (
                    product.productType === ItemType.INLAY_CORE ||
                    product.productType === ItemType.CROWN ||
                    product.productType === ItemType.LAY ||
                    product.productType === ItemType.SPLINT ||
                    product.productType === ItemType.PARTIAL_DENTURE ||
                    product.productType === ItemType.FULL_DENTURE ||
                    product.productType === ItemType.IMMEDIATE_DENTURE ||
                    product.productType === ItemType.FULL_DENTURE_WAX_BITE ||
                    product.productType === ItemType.IMPLANT ||
                    product.productType === ItemType.IMPLANT_CROWN ||
                    product.productType === ItemType.BRIDGE ||
                    product.productType === ItemType.BRIDGE_IMPLANT ||
                    product.productType === ItemType.SCAN_ONLY ||
                    product.productType === ItemType.VENEER
                  ) {
                    navigate(
                      `/orders/${order_id}/edit/${product.id}/edit?itemType=${product.productType}`,
                      {
                        state: {
                          product: product,
                        },
                      }
                    );
                    return;
                  }
                  setSelectedProductId(product.id);
                }}
              >
                Update product detail
              </Text>
              <Text
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => deleteProduct(product.id)}
              >
                Delete product
              </Text>
              <div style={{ marginLeft: "3rem" }}>
                <AnatomyItemView
                  product={product}
                  onDelete={itemId => deleteAnatomyItem(itemId)}
                  onUpdateTeeth={async (itemId, teeth) => {
                    try {
                      await updateAnatomyItem({
                        variables: {
                          where: {
                            id: itemId,
                          },
                          data: {
                            teeth: teeth,
                          },
                        },
                      });
                      notifications.show({
                        title: "Teeth updated",
                        color: "green",
                        message: "",
                      });
                      refetch();
                    } catch (e) {
                      notifications.show({
                        title: "Error while trying to update teeth of item",
                        color: "red",
                        message: `${e}`,
                      });
                    }
                    refetch();
                  }}
                />
                <RemovableItemView
                  product={product}
                  onDelete={itemId => deleteRemovableItem(itemId)}
                  onUpdateTeeth={async (itemId, teeth) => {
                    try {
                      await updateRemovableItem({
                        variables: {
                          where: {
                            id: itemId,
                          },
                          data: {
                            teeth: teeth,
                          },
                        },
                      });
                      notifications.show({
                        title: "Teeth updated",
                        color: "green",
                        message: "",
                      });
                      refetch();
                    } catch (e) {
                      notifications.show({
                        title: "Error while trying to update teeth of item",
                        color: "red",
                        message: `${e}`,
                      });
                    }
                  }}
                />
                <ImplantItemView
                  product={product}
                  onDelete={itemId => deleteImplantItem(itemId)}
                  onUpdateTeeth={async (itemId, teeth) => {
                    try {
                      await updateImplantItem({
                        variables: {
                          where: {
                            id: itemId,
                          },
                          data: {
                            teeth: teeth,
                          },
                        },
                      });
                      notifications.show({
                        title: "Teeth updated",
                        color: "green",
                        message: "",
                      });
                      refetch();
                    } catch (e) {
                      notifications.show({
                        title: "Error while trying to update teeth of item",
                        color: "red",
                        message: `${e}`,
                      });
                    }
                    refetch();
                  }}
                />
              </div>
            </div>
          );
        }
      )}
      <Space h="md" />
      <div style={{ textAlign: "center" }}>
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={() => {
            setSelectedProductId("");
            setIsModalNewProductOpen(true);
          }}
        >
          Add a new product
        </Button>
      </div>
    </>
  );
};

export default OrderEdit;
